import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getModelDetail } from "../lib/api.js";
import type {
  ModelDetail,
  ModelDetailComponentGroup,
  ModelDetailSubgroup,
} from "../lib/types.js";
import { Breadcrumbs } from "../ui/Breadcrumbs.js";
import { Badge } from "../ui/Badge.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import {
  ImageOffIcon,
  ChevronRightIcon,
  PackageIcon,
  CopyIcon,
  DocumentIcon,
  SearchIcon,
  CloseIcon,
} from "../lib/icons.js";
import { pushRecent } from "./HomePage.js";
import {
  CompatiblePowerpartsSection,
  ModelAssetsSection,
  useModelPowerparts,
  powerpartsGroupAnchor,
} from "./ModelPimExtras.js";
import type { ModelPowerpartGroup } from "../lib/types.js";

/**
 * Obrazovka C — detail modelu.
 *
 * Layout:
 *   - Hero (obrázek + název + brand/rok/article number + kopírovat)
 *   - Levý sloupec: komponentové skupiny jako kotvy
 *   - Hlavní obsah: pro každou skupinu grid podskupin (výkresů)
 */
export function ModelDetailPage(): JSX.Element {
  const { modelId } = useParams<{ modelId: string }>();
  const [model, setModel] = useState<ModelDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  // Fulltext filter pro kompatibilní PowerParts. Debounced niže v ModelBody.
  const [partsSearch, setPartsSearch] = useState("");
  const [partsMatchCount, setPartsMatchCount] = useState<number | null>(null);

  useEffect(() => {
    if (!modelId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getModelDetail(modelId)
      .then((m) => {
        if (cancelled) return;
        setModel(m);
        if (m.componentGroups[0]) setActiveGroup(m.componentGroups[0].id);
        pushRecent({
          url: `/model/${m.id}`,
          label: m.description ?? "Model",
          subtitle: [m.brand.shortName, m.year].filter(Boolean).join(" · "),
        });
      })
      .catch((err) => !cancelled && setError(String(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [modelId]);

  if (loading) return <LoadingBlock label="Načítám detail modelu…" />;
  if (error)
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          {error}
        </div>
      </div>
    );
  if (!model)
    return (
      <EmptyState title="Model nenalezen" description="Ověřte URL nebo se vraťte na vyhledávání." />
    );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6">
        <Breadcrumbs
          items={[
            { label: model.brand.shortName, to: "/search/model" },
            { label: model.description ?? "Model" },
          ]}
        />
      </div>

      <ModelHero model={model} />

      <PartsSearchBar
        value={partsSearch}
        onChange={setPartsSearch}
        matchCount={partsMatchCount}
      />

      <ModelBody
        model={model}
        activeGroup={activeGroup}
        setActiveGroup={setActiveGroup}
        partsSearch={partsSearch}
        onPartsMatchCount={setPartsMatchCount}
      />
    </div>
  );
}

/**
 * Search bar pro fulltextové filtrování kompatibilních PowerParts.
 * Debounced 250 ms, prázdný query = bez filtru.
 * Když má výsledky, scrolluje uživatele automaticky na PowerParts sekci.
 */
function PartsSearchBar({
  value,
  onChange,
  matchCount,
}: {
  value: string;
  onChange: (v: string) => void;
  matchCount: number | null;
}): JSX.Element {
  return (
    <div className="mt-6 flex items-center gap-3">
      <div className="relative flex-1">
        <SearchIcon
          size={16}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400"
        />
        <input
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Hledat kompatibilní díly — např. Akrapovič, brzdy, řídítka…"
          className="w-full h-11 pl-10 pr-10 rounded-xl border border-zinc-200 bg-white text-sm focus:border-ktm-orange focus:ring-2 focus:ring-ktm-orange-100 focus:outline-none"
        />
        {value.length > 0 && (
          <button
            type="button"
            onClick={() => onChange("")}
            aria-label="Vymazat hledání"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 transition-colors"
          >
            <CloseIcon size={16} />
          </button>
        )}
      </div>
      {value.length > 0 && matchCount != null && (
        <div className="text-sm text-zinc-500 tabular-nums whitespace-nowrap">
          {matchCount === 0
            ? "Žádný výsledek"
            : `${matchCount.toLocaleString("cs-CZ")} ${
                matchCount === 1 ? "díl" : matchCount < 5 ? "díly" : "dílů"
              }`}
        </div>
      )}
    </div>
  );
}

/**
 * Tělo detailu — levý nav + seznam skupin.
 * Řeší race condition mezi "klik v nav" a "scroll-based observer":
 *   - po kliku nastavíme `suppressObserverUntilRef.current = now+700ms`
 *   - observer update ignoruje všechny události do té doby
 */
function ModelBody({
  model,
  activeGroup,
  setActiveGroup,
  partsSearch,
  onPartsMatchCount,
}: {
  model: ModelDetail;
  activeGroup: string | null;
  setActiveGroup: (id: string) => void;
  partsSearch: string;
  onPartsMatchCount: (n: number | null) => void;
}): JSX.Element {
  const suppressUntil = useRef(0);
  // Centralizovaný fetch PowerParts — sdílený mezi navigací a hlavním obsahem.
  const { data: powerpartsData, loading: powerpartsLoading } =
    useModelPowerparts(model.id);

  // Debounce search query (250 ms)
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(partsSearch.trim().toLowerCase()), 250);
    return () => clearTimeout(t);
  }, [partsSearch]);

  // Filtrace skupin podle search query
  const filteredPowerpartsData = useMemo(() => {
    if (!powerpartsData) return powerpartsData;
    if (!debouncedSearch) return powerpartsData;
    const q = debouncedSearch;
    const groups = powerpartsData.groups
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (it) =>
            it.articleNumber.toLowerCase().includes(q) ||
            (it.name ?? "").toLowerCase().includes(q) ||
            (it.descriptionShort ?? "").toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.items.length > 0);
    const totalCompatibleArticles = groups.reduce(
      (sum, g) => sum + g.items.length,
      0,
    );
    return { ...powerpartsData, groups, totalCompatibleArticles };
  }, [powerpartsData, debouncedSearch]);

  // Notify parent o počtu výsledků (pro counter v search baru)
  useEffect(() => {
    if (!debouncedSearch) {
      onPartsMatchCount(null);
      return;
    }
    onPartsMatchCount(filteredPowerpartsData?.totalCompatibleArticles ?? 0);
  }, [debouncedSearch, filteredPowerpartsData, onPartsMatchCount]);

  // Auto-scroll na PowerParts sekci při prvním zadání query (když jsou výsledky)
  const hadSearchRef = useRef(false);
  useEffect(() => {
    const hasQuery = debouncedSearch.length > 0;
    const hasResults = (filteredPowerpartsData?.totalCompatibleArticles ?? 0) > 0;
    if (hasQuery && hasResults && !hadSearchRef.current) {
      hadSearchRef.current = true;
      const el = document.getElementById("group-__powerparts");
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (!hasQuery) hadSearchRef.current = false;
  }, [debouncedSearch, filteredPowerpartsData]);

  const handleSelect = useCallback(
    (id: string) => {
      suppressUntil.current = Date.now() + 700;
      setActiveGroup(id);
      // Speciální handling: id může být buď "group-<id>" skupina výkresů,
      // "__powerparts", "__assets", nebo "__pp-<slug>" (PowerParts subkategorie).
      const domId = id.startsWith("__pp-")
        ? "group-" + id
        : "group-" + id;
      const el = document.getElementById(domId);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [setActiveGroup],
  );

  const handleScrolledInto = useCallback(
    (id: string) => {
      if (Date.now() < suppressUntil.current) return;
      setActiveGroup(id);
    },
    [setActiveGroup],
  );

  const handleSubgroupEnterView = useCallback(
    (anchorId: string) => {
      // anchorId je "group-__pp-<slug>", active state používá id bez "group-" prefixu
      if (Date.now() < suppressUntil.current) return;
      const id = anchorId.replace(/^group-/, "");
      setActiveGroup(id);
    },
    [setActiveGroup],
  );

  return (
    <div className="mt-10 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
      {/*
        ⛔ Omezení výšky a vlastní posuvník patří JEN na počítač, kde je seznam
        skupin přilepený vedle obsahu. Bez `lg:` z toho na mobilu byl box vysoký
        712 px na 808px displeji s obsahem 998 px — musel ses prorolovat uvnitř
        boxu, než ses vůbec dostal k výkresům (změřeno 15. 8. 2026).
        Na mobilu se seznam prostě vypíše celý a scrolluje s stránkou.
      */}
      <aside className="lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto min-w-0">
        <GroupNav
          groups={model.componentGroups}
          active={activeGroup}
          onSelect={handleSelect}
          powerpartsGroups={filteredPowerpartsData?.groups ?? []}
        />
      </aside>

      <div className="space-y-12 min-w-0">
        {model.componentGroups.map((g) => (
          <ComponentGroupSection
            key={g.id}
            group={g}
            modelId={model.id}
            onEnterView={() => handleScrolledInto(g.id)}
          />
        ))}
        {model.componentGroups.length === 0 && (
          <EmptyState
            icon={<PackageIcon size={28} />}
            title="Pro tento model zatím nejsou výkresy"
            description="Data se stále synchronizují — zkuste to za chvíli."
          />
        )}

        {/* PIM-rozšíření: PowerParts + dokumenty. Sekce se samy schovají
            pokud pro tento model nejsou data. */}
        <CompatiblePowerpartsSection
          data={filteredPowerpartsData}
          loading={powerpartsLoading}
          onEnterView={() => handleScrolledInto("__powerparts")}
          onEnterSubgroup={handleSubgroupEnterView}
        />
        <ModelAssetsSection
          modelId={model.id}
          onEnterView={() => handleScrolledInto("__assets")}
        />
      </div>
    </div>
  );
}

function ModelHero({ model }: { model: ModelDetail }): JSX.Element {
  const [copied, setCopied] = useState(false);

  async function copyArticleNumber(): Promise<void> {
    if (!model.articleNumber) return;
    try {
      await navigator.clipboard.writeText(model.articleNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }

  const totalSubgroups = model.componentGroups.reduce(
    (sum, g) => sum + g.subgroups.length,
    0,
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-8 items-start">
      <div className="aspect-[5/3] bg-white rounded-2xl border border-zinc-200 shadow-card overflow-hidden flex items-center justify-center">
        {model.imageUrl ? (
          <img
            src={model.imageUrl}
            alt={model.description ?? ""}
            className="w-full h-full object-contain p-4"
          />
        ) : (
          <ImageOffIcon size={40} className="text-zinc-300" />
        )}
      </div>

      <div>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Badge tone="orange">{model.brand.shortName}</Badge>
          {model.year && <Badge>{model.year}</Badge>}
          {model.specifications.map((s) => (
            <Badge key={s.id}>{s.name}</Badge>
          ))}
          {model.isWpArticle && <Badge tone="blue">WP</Badge>}
        </div>
        <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 text-balance">
          {model.description ?? "Model bez názvu"}
        </h1>
        {model.color && (
          <p className="mt-1 text-zinc-500">{model.color}</p>
        )}

        <dl className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-5 max-w-2xl">
          <MetaItem label="Article number">
            {model.articleNumber ? (
              <button
                type="button"
                onClick={copyArticleNumber}
                className="inline-flex items-center gap-1.5 font-mono text-zinc-900 hover:text-ktm-orange transition-colors"
                title="Kopírovat"
              >
                {model.articleNumber}
                <CopyIcon size={14} className="text-zinc-400" />
                {copied && <span className="text-xs text-emerald-600">Zkopírováno</span>}
              </button>
            ) : (
              <span className="text-zinc-400">—</span>
            )}
          </MetaItem>
          <MetaItem label="Motor">
            {model.engineArticleNumber ? (
              <span className="font-mono">{model.engineArticleNumber}</span>
            ) : (
              <span className="text-zinc-400">—</span>
            )}
          </MetaItem>
          <MetaItem label="Skupiny">
            <span className="font-semibold text-zinc-900">
              {model.componentGroups.length}
            </span>
          </MetaItem>
          <MetaItem label="Výkresy">
            <span className="font-semibold text-zinc-900">{totalSubgroups}</span>
          </MetaItem>
        </dl>
      </div>
    </div>
  );
}

function MetaItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-zinc-700">{children}</dd>
    </div>
  );
}

function GroupNav({
  groups,
  active,
  onSelect,
  powerpartsGroups,
}: {
  groups: ModelDetailComponentGroup[];
  active: string | null;
  onSelect: (id: string) => void;
  powerpartsGroups: ModelPowerpartGroup[];
}): JSX.Element {
  // PowerParts je rozbalený pokud je aktivní sám, některá subkategorie, nebo máme > 0 subkategorií.
  const ppActiveId = active?.startsWith("__pp-") ? active : null;
  const ppHeaderActive = active === "__powerparts" || ppActiveId !== null;
  const ppExpanded = powerpartsGroups.length > 0;

  return (
    <nav className="space-y-1">
      <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium px-3 mb-2">
        Skupiny
      </div>
      {groups.map((g) => {
        const isActive = active === g.id;
        return (
          <button
            type="button"
            key={g.id}
            onClick={(e) => {
              e.preventDefault();
              onSelect(g.id);
            }}
            className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
              isActive
                ? "bg-ktm-orange-50 text-ktm-orange-700 font-medium"
                : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
            }`}
          >
            <span
              className={`w-1 h-5 rounded-full ${
                isActive ? "bg-ktm-orange" : "bg-transparent"
              }`}
            />
            <span className="flex-1 truncate">{g.description}</span>
            <span className="text-xs text-zinc-400">{g.subgroups.length}</span>
          </button>
        );
      })}

      {/* Pseudo-skupiny z PIM — oddělené separátorem */}
      <div className="pt-3 mt-3 border-t border-zinc-100" />
      <PseudoGroupButton
        id="__powerparts"
        active={ppHeaderActive ? "__powerparts" : active}
        onSelect={onSelect}
        icon={<PackageIcon size={14} />}
        label="PowerParts"
        count={powerpartsGroups.length || undefined}
      />
      {ppExpanded && (
        <div className="ml-6 space-y-0.5 border-l border-zinc-100 pl-2">
          {powerpartsGroups.map((g) => {
            const slug = powerpartsGroupAnchor(g.key).replace(/^group-/, "");
            const isActive = active === slug;
            const label = g.hierarchyL3 ?? categoryLabelShort(g.pimCategory);
            return (
              <button
                key={g.key}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  onSelect(slug);
                }}
                className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px] transition-colors ${
                  isActive
                    ? "bg-ktm-orange-50 text-ktm-orange-700 font-medium"
                    : "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
                }`}
                title={`${label} — ${g.items.length}`}
              >
                <span className="flex-1 truncate">{label}</span>
                <span className="text-[11px] text-zinc-400">{g.items.length}</span>
              </button>
            );
          })}
        </div>
      )}
      <PseudoGroupButton
        id="__assets"
        active={active}
        onSelect={onSelect}
        icon={<DocumentIcon size={14} />}
        label="Dokumenty"
      />
    </nav>
  );
}

function categoryLabelShort(c: string): string {
  return (
    {
      powerparts: "PowerParts",
      powerwear: "PowerWear",
      powerwear_collection: "PW Collection",
      plastic_kit: "Plastic Kit",
      wp_aftermarket: "WP Aftermarket",
      sparepart: "Náhradní díl",
      other: "Ostatní",
    }[c] ?? c
  );
}

function PseudoGroupButton({
  id,
  active,
  onSelect,
  icon,
  label,
  count,
}: {
  id: string;
  active: string | null;
  onSelect: (id: string) => void;
  icon: React.ReactNode;
  label: string;
  count?: number | undefined;
}): JSX.Element {
  const isActive = active === id;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        onSelect(id);
      }}
      className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
        isActive
          ? "bg-ktm-orange-50 text-ktm-orange-700 font-medium"
          : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
      }`}
    >
      <span
        className={`w-1 h-5 rounded-full ${
          isActive ? "bg-ktm-orange" : "bg-transparent"
        }`}
      />
      <span className="text-zinc-400">{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {count != null && (
        <span className="text-xs text-zinc-400">{count}</span>
      )}
    </button>
  );
}

function ComponentGroupSection({
  group,
  modelId,
  onEnterView,
}: {
  group: ModelDetailComponentGroup;
  modelId: string;
  onEnterView: () => void;
}): JSX.Element {
  const sectionRef = useRef<HTMLElement>(null);
  // Uložit nejnovější callback do ref — eliminuje dependency cycle a zabrání
  // tomu, aby se observer re-vytvářel při každém renderu parenta.
  const onEnterViewRef = useRef(onEnterView);
  useEffect(() => {
    onEnterViewRef.current = onEnterView;
  }, [onEnterView]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            onEnterViewRef.current();
            break;
          }
        }
      },
      { rootMargin: "-40% 0px -50% 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section id={`group-${group.id}`} ref={sectionRef} className="scroll-mt-24">
      <div className="flex items-end justify-between mb-4 gap-4">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold text-zinc-900">
            {group.description}
          </h2>
          <p className="mt-0.5 text-sm text-zinc-500">
            {group.subgroups.length}{" "}
            {group.subgroups.length === 1
              ? "výkres"
              : group.subgroups.length < 5
                ? "výkresy"
                : "výkresů"}{" "}
            · {group.code}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {group.subgroups.map((sg) => (
          <SubgroupCard key={sg.id} subgroup={sg} modelId={modelId} />
        ))}
      </div>
    </section>
  );
}

function SubgroupCard({
  subgroup,
  modelId,
}: {
  subgroup: ModelDetailSubgroup;
  modelId: string;
}): JSX.Element {
  return (
    <Link
      to={`/model/${modelId}/drawing/${subgroup.id}`}
      className="group bg-white rounded-xl border border-zinc-200 shadow-card hover:shadow-card-hover hover:border-ktm-orange-100 transition-all overflow-hidden flex flex-col"
    >
      <div className="aspect-square bg-zinc-50 flex items-center justify-center border-b border-zinc-100 overflow-hidden">
        {subgroup.explosionImageUrl ? (
          <img
            src={subgroup.explosionImageUrl}
            alt={subgroup.description}
            className="max-w-full max-h-full object-contain p-2 group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <ImageOffIcon size={28} className="text-zinc-300" />
        )}
      </div>
      <div className="p-3 flex items-start justify-between gap-2 min-h-[56px]">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-zinc-400 font-mono mb-0.5">
            {subgroup.code}
          </div>
          <div className="text-sm text-zinc-900 leading-tight line-clamp-2 group-hover:text-ktm-orange transition-colors">
            {subgroup.description}
          </div>
        </div>
        <ChevronRightIcon
          size={14}
          className="text-zinc-300 group-hover:text-ktm-orange group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-0.5"
        />
      </div>
    </Link>
  );
}
